/**
 * STYLING: Plain Tailwind ONLY!
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/v2/ui';
import { TextToolVariantSwitcher } from './TextToolVariantSwitcher';
import { TextToolRefineBar } from './TextToolRefineBar';
import type { RefineIntent, TextGenerateResponse } from './textGenerationApi';

interface TextToolResultProps {
  toolCode: string;
  result: TextGenerateResponse;
  onRefine?: (intent: RefineIntent, sourceContent: string) => void;
  refining?: boolean;
}

export function TextToolResult({ toolCode, result, onRefine, refining }: TextToolResultProps) {
  const { t } = useTranslation();
  const variants = result.variants?.length ? result.variants : [result];
  const [active, setActive] = useState(0);
  const safeActive = Math.min(active, variants.length - 1);

  return (
    <Card id={`text-tool-result-${toolCode}`}>
      <CardHeader>
        <CardTitle>{t('textTools.result')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TextToolVariantSwitcher
          toolCode={toolCode}
          variants={variants}
          active={safeActive}
          onActiveChange={setActive}
        />
        {(result.failed_count ?? 0) > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400" role="status">
            {t('textTools.variants.failed', {
              count: result.failed_count,
              credits: result.refunded_credits ?? 0,
            })}
          </p>
        )}
        {onRefine && (
          <TextToolRefineBar
            onRefine={(intent) => onRefine(intent, variants[safeActive].content)}
            refining={!!refining}
          />
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label={t('textTools.credits')} value={result.credits_charged} color="amber" />
          <StatBox label={t('textTools.inputTokens')} value={result.token_usage?.input_tokens ?? 0} color="primary" />
          <StatBox label={t('textTools.outputTokens')} value={result.token_usage?.output_tokens ?? 0} color="green" />
          <StatBox label={t('textTools.model')} value={result.model} color="neutral" isText />
        </div>
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value, color, isText }: { label: string; value: number | string; color: string; isText?: boolean }) {
  return (
    <div className={`bg-${color}-50 dark:bg-${color}-950/20 rounded-lg p-3`}>
      <p className="text-xs text-neutral-600 dark:text-neutral-400">{label}</p>
      <p className={`${isText ? 'text-xs' : 'text-lg'} font-semibold text-${color}-900 dark:text-${color}-100`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  );
}
